import { Node, ResolvedPos } from 'prosemirror-model'
import { Command } from 'prosemirror-commands'
import { liftListItem, splitListItem } from 'prosemirror-schema-list'
import { MAX_LIST_DEPTH, textboxSchema } from './textboxSchema'

// List editing for the scoped text-box schema. Unlike the shared doc-editor commands (which compare
// node types against prose-core's schema SINGLETON and so silently no-op against a different schema),
// these match list items by type NAME, so they work against the text box's own schema. Nesting is a
// flat `depth` attribute on the list item (matching the renderer + CSS), not nested <ul> DOM.

// The enclosing list_item and the resolved-position depth level at which it sits.
const enclosingListItem = ($pos: ResolvedPos): { depth: number; level: number } | null => {
  for (let level = $pos.depth; level > 0; level--) {
    const node = $pos.node(level)
    if (node.type.name === 'list_item') {
      return { depth: typeof node.attrs.depth === 'number' ? node.attrs.depth : 0, level }
    }
  }
  return null
}

const setItemDepth =
  (depth: number): Command =>
  (state, dispatch) => {
    const item = enclosingListItem(state.selection.$from)
    if (!item) return false
    const next = Math.max(0, Math.min(MAX_LIST_DEPTH, depth))
    if (next === item.depth) return false
    if (dispatch) {
      const pos = state.selection.$from.before(item.level)
      const node = state.doc.nodeAt(pos)
      if (node) dispatch(state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, depth: next }))
    }
    return true
  }

// Tab / Shift-Tab: bump the list item's `depth` attribute (clamped). No-op outside a list.
export const changeListIndent =
  (direction: 1 | -1): Command =>
  (state, dispatch) => {
    const item = enclosingListItem(state.selection.$from)
    if (!item) return false
    return setItemDepth(item.depth + direction)(state, dispatch)
  }

// Enter on an EMPTY list item: un-indent a level, then (at depth 0) lift it out of the list to a
// paragraph. Returns false for a non-empty item so the chained splitListItem makes a new row instead.
export const exitEmptyListItem: Command = (state, dispatch) => {
  const { $from, empty } = state.selection
  if (!empty) return false
  const item = enclosingListItem($from)
  if (!item || $from.node(item.level).textContent.length > 0) return false
  if (item.depth > 0) return setItemDepth(item.depth - 1)(state, dispatch)
  return liftListItem(textboxSchema.nodes.list_item)(state, dispatch)
}

// Backspace at the START of an INDENTED list item: outdent one level. A depth-0 item (or a paragraph)
// returns false and falls through to deleteBackward, which merges it into the previous line. Crucially
// this does NOT lift a depth-0 bullet out to a paragraph — that lift, paired with the base keymap's
// joinBackward rejoining the paragraph as a new bullet, is what oscillated forever.
export const outdentAtStart: Command = (state, dispatch) => {
  const { $from, empty } = state.selection
  if (!empty || $from.parentOffset !== 0) return false
  const item = enclosingListItem($from)
  if (!item || item.depth === 0) return false
  return setItemDepth(item.depth - 1)(state, dispatch)
}

// Enter: exit an empty item, else split into a new row (depth carried by splitListItem).
export const splitTextboxListItem: Command = splitListItem(textboxSchema.nodes.list_item)

// Forward Delete at the END of a textblock: pull the NEXT textblock's content up into this one, in
// document order, whatever wrapper it sits in (a following paragraph, or the next list item). This is
// deterministic — it always merges the next line's text — so it can't fall into the base keymap's
// lift/rejoin oscillation at a list↔paragraph boundary (empty bullet ⇄ empty trailing paragraph). When
// nothing follows, it returns false and does nothing (the box just ends). Mid-textblock Delete also
// returns false, so the base keymap deletes the next character normally.
export const deleteForward: Command = (state, dispatch) => {
  const { $from, empty } = state.selection
  if (!empty || !$from.parent.isTextblock) return false
  if ($from.parentOffset !== $from.parent.content.size) return false // not at the end of the textblock

  // The first textblock that starts after the cursor's block, at any depth.
  let nextPos = -1
  state.doc.nodesBetween($from.after(), state.doc.content.size, (node: Node, pos: number) => {
    if (nextPos !== -1) return false
    if (node.isTextblock) nextPos = pos
    return nextPos === -1
  })
  if (nextPos === -1) return false // nothing after — leave the box as-is

  // Delete the boundary between the two textblocks; replacing with an empty slice joins the next
  // block's inline content into this one and unwraps any list item / list it emptied.
  if (dispatch) dispatch(state.tr.delete($from.pos, nextPos + 1).scrollIntoView())
  return true
}

// Backspace at the START of a textblock: merge this block INTO the previous textblock (document order),
// whatever wrapper it sits in. The mirror of deleteForward — deterministic, so it can't oscillate. When
// nothing precedes it returns false (the base keymap handles the start-of-doc / exit-list edge).
export const deleteBackward: Command = (state, dispatch) => {
  const { $from, empty } = state.selection
  if (!empty || !$from.parent.isTextblock) return false
  if ($from.parentOffset !== 0) return false // not at the start of the textblock

  // The last textblock that ends before this block starts.
  let prevEnd = -1
  state.doc.nodesBetween(0, $from.before(), (node: Node, pos: number) => {
    if (node.isTextblock) prevEnd = pos + node.nodeSize
    return true
  })
  if (prevEnd === -1) return false // nothing before — let the base keymap handle the edge

  // Delete the boundary between the previous textblock's content end and this block's content start:
  // joins this block's inline content into the previous one and unwraps any list item / list it emptied.
  if (dispatch) dispatch(state.tr.delete(prevEnd - 1, $from.pos).scrollIntoView())
  return true
}
